'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import {
  Building2, ArrowLeft, TrendingDown, TrendingUp,
  CreditCard, Calendar, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, Info, ArrowUpDown
} from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { mockComparisonReport } from '@/lib/mockData';
import { LoanComparison } from '@/lib/types';
import StatusBadge from '@/components/ui/StatusBadge';
import MetricCard from '@/components/ui/MetricCard';
import EmptyState from '@/components/ui/EmptyState';
import { formatEuro, formatEuroCompact, formatDate, formatPercent } from '@/lib/formatters';
import LoanBalanceChart from './LoanBalanceChart';

type LoanSortKey = 'produtoFinanceiro' | 'inicio' | 'fim' | 'saldoAnterior' | 'saldoAtual' | 'diferenca' | 'status';
type SortDir = 'asc' | 'desc';

export default function InstitutionDetailContent() {
  const { report: storeReport, selectedInstitutionId } = useAppStore();
  const report = storeReport ?? mockComparisonReport;

  const institution = useMemo(() => {
    if (selectedInstitutionId) {
      return report.institutions.find((i) => i.institutionId === selectedInstitutionId)
        ?? report.institutions[0];
    }
    return report.institutions[0];
  }, [report, selectedInstitutionId]);

  const [sortKey, setSortKey] = useState<LoanSortKey>('diferenca');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedSection, setExpandedSection] = useState<'novos' | 'liquidados' | null>(null);

  const handleSort = (key: LoanSortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sortedLoans = useMemo(() => {
    if (!institution) return [];
    return [...institution.loans].sort((a, b) => {
      const av = a[sortKey] as string | number | null;
      const bv = b[sortKey] as string | number | null;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'pt') : bv.localeCompare(av, 'pt');
      }
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [institution, sortKey, sortDir]);

  const novosEmprestimos = institution?.loans.filter((l) => l.status === 'novo') ?? [];
  const liquidadosEmprestimos = institution?.loans.filter((l) => l.status === 'liquidado') ?? [];
  const activosEmprestimos = institution?.loans.filter((l) => l.status === 'activo' || l.status === 'alterado') ?? [];

  if (!institution) {
    return (
      <div className="fade-in">
        <EmptyState
          icon={<Building2 size={22} />}
          title="Nenhuma instituição seleccionada"
          description="Seleccione uma instituição no Relatório Comparativo para ver o detalhe dos empréstimos."
          action={
            <Link href="/comparison-report" className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary/90 transition-all duration-150">
              Ver Relatório Comparativo
            </Link>
          }
        />
      </div>
    );
  }

  const hasAnomalia = institution.status === 'anomalia';

  return (
    <div className="fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link href="/comparison-report" className="hover:text-primary transition-colors duration-150 flex items-center gap-1">
          <ArrowLeft size={14} />
          Relatório Comparativo
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium">{institution.nome}</span>
      </div>

      {/* Institution header */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 size={22} className="text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-semibold text-foreground">{institution.nome}</h1>
                <StatusBadge status={institution.status} size="md" />
              </div>
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CreditCard size={13} />
                  {institution.numEmprestimosAtual} empréstimos activos
                </span>
                {novosEmprestimos.length > 0 && (
                  <span className="flex items-center gap-1.5 text-xs text-[color:var(--info)]">
                    <CheckCircle2 size={13} />
                    {novosEmprestimos.length} novo(s)
                  </span>
                )}
                {liquidadosEmprestimos.length > 0 && (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 size={13} />
                    {liquidadosEmprestimos.length} liquidado(s)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Period comparison */}
          <div className="flex items-center gap-6 bg-muted/40 rounded-lg px-5 py-3">
            <div className="text-center">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{report.mesAnterior}</p>
              <p className="text-lg font-bold tabular-nums text-foreground">{formatEuroCompact(institution.saldoAnterior)}</p>
            </div>
            <div className="flex flex-col items-center">
              <TrendingDown size={18} className={institution.valorPago >= 0 ? 'text-positive' : 'text-negative'} />
              <span className={`text-xs font-semibold tabular-nums mt-0.5 ${institution.variacaoPercent < 0 ? 'text-positive' : institution.variacaoPercent > 0 ? 'text-negative' : 'text-muted-foreground'}`}>
                {formatPercent(institution.variacaoPercent)}
              </span>
            </div>
            <div className="text-center">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{report.mesAtual}</p>
              <p className="text-lg font-bold tabular-nums text-foreground">{formatEuroCompact(institution.saldoAtual)}</p>
            </div>
          </div>
        </div>

        {/* Anomalia warning */}
        {hasAnomalia && (
          <div className="flex items-start gap-3 bg-warning-subtle border border-amber-200 rounded-lg px-4 py-3 mt-4">
            <AlertTriangle size={15} className="text-warning-color mt-0.5 shrink-0" />
            <p className="text-sm text-warning-color">
              Esta instituição apresenta uma anomalia: o saldo não diminuiu ou aumentou entre os dois meses. Verifique os empréstimos marcados como &quot;Alterado&quot; na tabela abaixo.
            </p>
          </div>
        )}
      </div>

      {/* KPI row — 3 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <MetricCard
          label="Valor Pago no Mês"
          value={institution.valorPago >= 0 ? formatEuro(institution.valorPago) : `(${formatEuro(Math.abs(institution.valorPago))})`}
          subValue={`${report.mesAnterior} → ${report.mesAtual}`}
          trend={institution.valorPago > 0 ? 'positive' : institution.valorPago < 0 ? 'negative' : 'warning'}
          trendLabel={institution.valorPago > 0 ? 'Pagamento confirmado' : institution.valorPago < 0 ? 'Saldo aumentou — anomalia' : 'Sem movimento no mês'}
          icon={<TrendingDown size={18} />}
        />
        <MetricCard
          label="Variação Percentual"
          value={formatPercent(institution.variacaoPercent)}
          subValue="Face ao mês anterior"
          trend={institution.variacaoPercent < 0 ? 'positive' : institution.variacaoPercent > 0 ? 'negative' : 'warning'}
          icon={institution.variacaoPercent <= 0 ? <TrendingDown size={18} /> : <TrendingUp size={18} />}
        />
        <MetricCard
          label="Empréstimos"
          value={`${institution.numEmprestimosAtual}`}
          subValue={`${institution.novosEmprestimos} novos · ${institution.emprestimosLiquidados} liquidados`}
          trend="neutral"
          icon={<CreditCard size={18} />}
        />
      </div>

      {/* Chart + summary side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <div className="xl:col-span-2 bg-card border border-border rounded-lg p-5">
          <h2 className="text-sm font-semibold text-foreground mb-1">Evolução por Empréstimo</h2>
          <p className="text-xs text-muted-foreground mb-4">Saldo anterior vs. saldo actual por produto financeiro</p>
          <LoanBalanceChart loans={institution.loans} />
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Info size={15} className="text-primary" />
            Resumo de Movimentos
          </h2>
          <div className="space-y-3">
            <SummaryRow label="Saldo anterior total" value={formatEuro(institution.saldoAnterior)} />
            <SummaryRow label="Saldo actual total" value={formatEuro(institution.saldoAtual)} />
            <div className="border-t border-border pt-3">
              <SummaryRow
                label="Diferença (valor pago)"
                value={institution.valorPago >= 0 ? formatEuro(institution.valorPago) : `(${formatEuro(Math.abs(institution.valorPago))})`}
                highlight={institution.valorPago > 0 ? 'positive' : institution.valorPago < 0 ? 'negative' : 'neutral'}
              />
            </div>
            <div className="border-t border-border pt-3">
              <SummaryRow label="Empréstimos activos" value={`${activosEmprestimos.length}`} />
              {novosEmprestimos.length > 0 && <SummaryRow label="Novos empréstimos" value={`${novosEmprestimos.length}`} highlight="positive" />}
              {liquidadosEmprestimos.length > 0 && <SummaryRow label="Liquidados" value={`${liquidadosEmprestimos.length}`} highlight="neutral" />}
            </div>
          </div>
        </div>
      </div>

      {/* Main loan table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden mb-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <CreditCard size={16} className="text-primary" />
            Detalhe por Empréstimo
            <span className="text-xs font-normal text-muted-foreground">({sortedLoans.length} empréstimos)</span>
          </h2>
          <div className="flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-md px-3 py-1.5">
            <Building2 size={13} className="text-primary shrink-0" />
            <span className="text-xs font-semibold text-primary">{institution.nome}</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b border-border">
                <LoanSortHeader label="Produto Financeiro" sortKey="produtoFinanceiro" current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                <LoanSortHeader label="Início" sortKey="inicio" current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                <LoanSortHeader label="Fim" sortKey="fim" current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                <LoanSortHeader label={`Saldo ${report.mesAnterior}`} sortKey="saldoAnterior" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <LoanSortHeader label={`Saldo ${report.mesAtual}`} sortKey="saldoAtual" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <LoanSortHeader label="Valor Pago" sortKey="diferenca" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <LoanSortHeader label="Estado" sortKey="status" current={sortKey} dir={sortDir} onSort={handleSort} align="center" />
              </tr>
            </thead>
            <tbody>
              {sortedLoans.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={<CreditCard size={22} />}
                      title="Nenhum empréstimo encontrado"
                      description="Esta instituição não tem empréstimos registados no período seleccionado."
                    />
                  </td>
                </tr>
              ) : (
                sortedLoans.map((loan, idx) => (
                  <tr
                    key={`loan-row-${loan.loanId}`}
                    className={`border-b border-border hover:bg-muted/30 transition-colors duration-100
                      ${idx % 2 === 0 ? '' : 'bg-muted/10'}
                      ${loan.status === 'alterado' ? 'bg-warning-subtle/30' : ''}
                      ${loan.status === 'liquidado' ? 'opacity-60' : ''}
                    `}
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <ProductTypeDot type={loan.produtoFinanceiro} />
                        <span className="font-medium text-foreground">{loan.produtoFinanceiro}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-muted-foreground flex items-center gap-1">
                      <Calendar size={12} className="shrink-0" />
                      {formatDate(loan.inicio)}
                    </td>
                    <td className="px-4 py-3.5 tabular-nums text-muted-foreground">{formatDate(loan.fim)}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">
                      {loan.saldoAnterior !== null ? formatEuro(loan.saldoAnterior) : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums font-medium text-foreground">
                      {loan.saldoAtual !== null ? formatEuro(loan.saldoAtual) : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className={`tabular-nums font-semibold ${loan.diferenca > 0 ? 'text-positive' : loan.diferenca < 0 ? 'text-negative' : 'text-muted-foreground'}`}>
                        {loan.diferenca !== 0
                          ? (loan.diferenca > 0 ? formatEuro(loan.diferenca) : `(${formatEuro(Math.abs(loan.diferenca))})`)
                          : '€ 0,00'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <StatusBadge status={loan.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-muted/40 border-t-2 border-border">
                <td className="px-4 py-3.5 text-sm font-semibold text-foreground" colSpan={3}>Total</td>
                <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                  {formatEuro(institution.saldoAnterior)}
                </td>
                <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                  {formatEuro(institution.saldoAtual)}
                </td>
                <td className="px-4 py-3.5 text-right tabular-nums font-bold text-positive">
                  {formatEuro(institution.loans.reduce((s, l) => s + l.diferenca, 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Novos empréstimos section */}
      {novosEmprestimos.length > 0 && (
        <CollapsibleSection
          title={`Novos Empréstimos (${novosEmprestimos.length})`}
          icon={<CheckCircle2 size={15} className="text-[color:var(--info)]" />}
          isOpen={expandedSection === 'novos'}
          onToggle={() => setExpandedSection(expandedSection === 'novos' ? null : 'novos')}
          colorClass="bg-info-subtle border-blue-200"
        >
          <LoanMiniTable loans={novosEmprestimos} mesAtual={report.mesAtual} />
        </CollapsibleSection>
      )}

      {/* Liquidados section */}
      {liquidadosEmprestimos.length > 0 && (
        <CollapsibleSection
          title={`Empréstimos Liquidados (${liquidadosEmprestimos.length})`}
          icon={<CheckCircle2 size={15} className="text-muted-foreground" />}
          isOpen={expandedSection === 'liquidados'}
          onToggle={() => setExpandedSection(expandedSection === 'liquidados' ? null : 'liquidados')}
          colorClass="bg-muted border-border"
        >
          <LoanMiniTable loans={liquidadosEmprestimos} mesAtual={report.mesAtual} />
        </CollapsibleSection>
      )}

      {/* Navigation footer */}
      <div className="mt-8 flex items-center justify-between pt-4 border-t border-border">
        <Link
          href="/comparison-report"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-150"
        >
          <ArrowLeft size={14} />
          Voltar ao Relatório Comparativo
        </Link>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Período: {report.mesAnterior} → {report.mesAtual}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SummaryRow({ label, value, highlight }: { label: string; value: string; highlight?: 'positive' | 'negative' | 'neutral' }) {
  const valueClass = highlight === 'positive' ? 'text-positive font-semibold' : highlight === 'negative' ? 'text-negative font-semibold' : 'text-foreground';
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-xs tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

function ProductTypeDot({ type }: { type: string }) {
  const colors: Record<string, string> = {
    'Crédito Habitação': 'bg-primary',
    'Crédito Pessoal': 'bg-accent',
    'Crédito Automóvel': 'bg-[color:var(--info)]',
    'Leasing Imobiliário': 'bg-purple-400',
    'Leasing Automóvel': 'bg-teal-400',
    'Crédito Empresas': 'bg-orange-400',
  };
  const color = colors[type] ?? 'bg-muted-foreground';
  return <span className={`w-2 h-2 rounded-full shrink-0 ${color}`} />;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  colorClass: string;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, isOpen, onToggle, colorClass, children }: CollapsibleSectionProps) {
  return (
    <div className={`border rounded-lg overflow-hidden mb-4 ${colorClass}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-black/5 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-foreground">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
      </button>
      {isOpen && (
        <div className="border-t border-border bg-card">
          {children}
        </div>
      )}
    </div>
  );
}

function LoanMiniTable({ loans, mesAtual }: { loans: LoanComparison[]; mesAtual: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/30 border-b border-border">
            <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Produto Financeiro</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Início</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Fim</th>
            <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Saldo {mesAtual}</th>
            <th className="text-center px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Estado</th>
          </tr>
        </thead>
        <tbody>
          {loans.map((loan) => (
            <tr key={`mini-${loan.loanId}`} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors duration-100">
              <td className="px-4 py-3 font-medium text-foreground">{loan.produtoFinanceiro}</td>
              <td className="px-4 py-3 tabular-nums text-muted-foreground">{formatDate(loan.inicio)}</td>
              <td className="px-4 py-3 tabular-nums text-muted-foreground">{formatDate(loan.fim)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-medium text-foreground">
                {loan.saldoAtual !== null ? formatEuro(loan.saldoAtual) : '—'}
              </td>
              <td className="px-4 py-3 text-center">
                <StatusBadge status={loan.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoanSortHeader({
  label, sortKey, current, dir, onSort, align
}: {
  label: string;
  sortKey: LoanSortKey;
  current: LoanSortKey;
  dir: SortDir;
  onSort: (k: LoanSortKey) => void;
  align: 'left' | 'right' | 'center';
}) {
  const isActive = current === sortKey;
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`px-4 py-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors duration-100 ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        <span className={isActive ? 'text-primary' : ''}>{label}</span>
        <ArrowUpDown size={11} className={isActive ? 'text-primary' : 'opacity-30'} />
      </span>
    </th>
  );
}